import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import { Pencil, RotateCcw } from "lucide-react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { cn } from "@/lib/utils";
import {
  canRename,
  isRenamed,
  normaliseRename,
  type RouterPort,
} from "./router-model";

export type PortKind = "source" | "target";

interface EditingPort {
  kind: PortKind;
  id: string;
}

export interface RenameApi {
  canRename: (kind: PortKind, port: RouterPort) => boolean;
  isEditing: (kind: PortKind, id: string) => boolean;
  start: (kind: PortKind, id: string) => void;
  cancel: () => void;
  commit: (kind: PortKind, port: RouterPort, value: string) => void;
  reset: (kind: PortKind, port: RouterPort) => void;
}

const RenameContext = createContext<RenameApi | null>(null);

export const useRename = (): RenameApi => {
  const ctx = useContext(RenameContext);
  if (!ctx) throw new Error("useRename must be used inside <Router>");
  return ctx;
};

interface RenameProviderProps {
  onRenameSource?: (id: string, name: string | null) => void;
  onRenameTarget?: (id: string, name: string | null) => void;
  disabled?: boolean;
  children: ReactNode;
}

export const RenameProvider = ({
  onRenameSource,
  onRenameTarget,
  disabled,
  children,
}: RenameProviderProps) => {
  const [editing, setEditing] = useState<EditingPort | null>(null);

  const handlerFor = (kind: PortKind) =>
    kind === "source" ? onRenameSource : onRenameTarget;

  const api: RenameApi = {
    canRename: (kind, port) => canRename(port, !!handlerFor(kind), disabled),
    isEditing: (kind, id) => editing?.kind === kind && editing.id === id,
    start: (kind, id) => setEditing({ kind, id }),
    cancel: () => setEditing(null),
    commit: (kind, port, value) => {
      const name = normaliseRename(port, value);
      if (name !== undefined) handlerFor(kind)?.(port.id, name);
      setEditing(null);
    },
    reset: (kind, port) => {
      handlerFor(kind)?.(port.id, null);
      setEditing(null);
    },
  };

  return (
    <RenameContext.Provider value={api}>{children}</RenameContext.Provider>
  );
};

/* ─────────────────────────────────────────────────────────── */
/* Building blocks                                              */
/* ─────────────────────────────────────────────────────────── */

interface RenameInputProps {
  kind: PortKind;
  port: RouterPort;
  className?: string;
}

/** Inline input: Enter saves, Escape (or blur) cancels, empty = reset. */
export const RenameInput = ({ kind, port, className }: RenameInputProps) => {
  const rename = useRename();
  const [value, setValue] = useState(port.label);
  const ref = useRef<HTMLInputElement>(null);
  const ready = useRef(false);

  // Focus after any closing menu/popover has finished restoring focus
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.select();
      ready.current = true;
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <input
      ref={ref}
      autoFocus
      value={value}
      placeholder={port.defaultLabel}
      aria-label={`Rename ${port.label}`}
      data-testid={`router-rename-input-${port.id}`}
      onChange={(e) => setValue(e.target.value)}
      onFocus={(e) => e.currentTarget.select()}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") rename.commit(kind, port, value);
        if (e.key === "Escape") rename.cancel();
      }}
      onBlur={() => {
        // Ignore focus shuffles while menus close; afterwards blur cancels
        if (ready.current) rename.cancel();
      }}
      className={cn(
        "h-6 min-w-0 rounded-sm border border-input bg-background px-1.5 text-xs font-normal text-foreground shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring",
        className,
      )}
    />
  );
};

interface PortMenuProps {
  kind: PortKind;
  port: RouterPort;
  /** Single element; receives the context-menu trigger props */
  children: ReactElement;
}

/** Wraps a label/button with the rename context menu (when renaming is allowed). */
export const PortMenu = ({ kind, port, children }: PortMenuProps) => {
  const rename = useRename();
  if (!rename.canRename(kind, port)) return children;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent
        className="w-44"
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <ContextMenuItem
          className="text-xs"
          onSelect={() => rename.start(kind, port.id)}
        >
          <Pencil className="mr-2 h-3.5 w-3.5" />
          Rename…
        </ContextMenuItem>
        {isRenamed(port) && (
          <ContextMenuItem
            className="text-xs"
            onSelect={() => rename.reset(kind, port)}
          >
            <RotateCcw className="mr-2 h-3.5 w-3.5" />
            Reset to default
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

/** Current label, plus the factory name in muted text when renamed. */
export const PortLabelText = ({
  port,
  showDefault = true,
  className,
}: {
  port: RouterPort;
  showDefault?: boolean;
  className?: string;
}) => (
  <span className={cn("truncate", className)} title={portTitle(port)}>
    {port.label}
    {showDefault && isRenamed(port) && (
      <span className="ml-1 text-muted-foreground font-normal opacity-80">
        ({port.defaultLabel})
      </span>
    )}
  </span>
);

export const portTitle = (port: RouterPort): string =>
  isRenamed(port) ? `${port.label} (${port.defaultLabel})` : port.label;

/** Double-click handler that starts renaming when allowed. */
export const useStartRenameOnDoubleClick = (
  kind: PortKind,
  port: RouterPort,
) => {
  const rename = useRename();
  return rename.canRename(kind, port)
    ? (e: MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        rename.start(kind, port.id);
      }
    : undefined;
};

interface RenamableLabelProps {
  kind: PortKind;
  port: RouterPort;
  className?: string;
  inputClassName?: string;
  showDefault?: boolean;
}

/** A text label that can be renamed inline (double-click or context menu). */
export const RenamableLabel = ({
  kind,
  port,
  className,
  inputClassName,
  showDefault = true,
}: RenamableLabelProps) => {
  const rename = useRename();
  const onDoubleClick = useStartRenameOnDoubleClick(kind, port);

  if (rename.isEditing(kind, port.id)) {
    return (
      <RenameInput
        kind={kind}
        port={port}
        className={cn("w-full", inputClassName)}
      />
    );
  }

  return (
    <PortMenu kind={kind} port={port}>
      <span
        data-testid={`router-${kind}-${port.id}`}
        onDoubleClick={onDoubleClick}
        className={cn(
          "block min-w-0 truncate select-none",
          port.disabled && "text-muted-foreground opacity-60",
          onDoubleClick && "cursor-text",
          className,
        )}
      >
        <PortLabelText port={port} showDefault={showDefault} />
      </span>
    </PortMenu>
  );
};
